/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { ACLS } from '@config/constants/acls';
import { AREC_ORGANIZATION_IDS } from '@config/constants/organizations';
import { ROLES } from '@config/constants/roles';
import { TMesurabilityProject } from '@features/resources/measurabilities/types';
import { TApiParams, TParams } from '@global-types/index';
import { apiClient } from '@lib/api-client';
import { useAuthorization } from '@lib/authorization';
import { useQuery } from '@tanstack/react-query';
import { getCountryName } from '@utils/countries';
import {
	formatCurrency,
	formatTokenAvailable,
} from '@utils/formats';
import {
	convertGCO2EToTCO2EWithoutUnit,
	convertTCO2EToGCO2EWithoutUnit,
} from '@utils/functions';
import { getTechnologyProject } from '@utils/project-technologies';
import get from 'lodash/get';
import mapValues from 'lodash/mapValues';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
export type TApiResponse = {
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
	rows: TMesurabilityProject[];
};

export type TParsedMesurabilityProject = {
	supportedBy?: string;
	entity: string;
	name: string;
	unit: string;
	technology: string;
	__t: string;
	sellPrice: number;
	sellPrice_europertco2eq: number;
	costPrice_europertco2eq: number;
	quantity: {
		total_aco2: number;
		held_aco2: number;
		burnt_aco2: number;
		remaining_aco2: number;
		unprocessed_gco2eq: number;
	};
	organization: string;
	location: {
		countryCode: string;
		place: string;
	};
	country?: {
		name: string;
		code: string;
	};
	region?: string;
	type?: string;
	createdAt: string;
	updatedAt: string;
	id: string;
	tokenAvailable?: string;
	availableStock: number;
	quantitytco2eq?: number;
	cover?: string;
	priceTco2?: string;
	image?: string;
	isDisabled: boolean;
	active: boolean;
};

type RangeFilter = {
	$gte?: number;
	$lte?: number;
	[key: string]: number | undefined;
};

type QueryParams = {
	[key: string]: unknown;
};

export function normalizeQueryParams(
	restQueryParams: QueryParams
): QueryParams {
	const path = 'quantity.remaining_aco2';

	const range = get(restQueryParams, path) as
		| RangeFilter
		| undefined;

	if (!range) {
		return restQueryParams;
	}

	const updatedRange = mapValues(range, (value, key) => {
		if (
			typeof value === 'number' &&
			(key === '$gte' || key === '$lte')
		) {
			return convertTCO2EToGCO2EWithoutUnit(value);
		}
		return value;
	});

	return {
		...restQueryParams,
		[path]: updatedRange,
	};
}

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getMeasurabilityProjects = (
	params: TApiParams,
	isArec: boolean
): Promise<TApiResponse> => {
	const { queryParams, page, pageSize } = params;
	const { name, ...restQueryParams } = queryParams;

	return apiClient.get('/measurabilities', {
		params: {
			page: page,
			pageSize: pageSize,
			query: normalizeQueryParams(restQueryParams),
			sort: `${isArec ? '-supportedBy ' : ''}-active -quantity.remaining_aco2 name`,
			...(name && {
				search: name,
				searchFields: ['name', 'location.place'],
			}),
			populate: `cover, logo, publicSpecs ${isArec ? `, supportedBy.logo` : ``}`,
		},
	});
};

export const getPublicMeasurabilityProjectsForBrands = (
	params: TApiParams,
	isArec: boolean
): Promise<TApiResponse> => {
	const { queryParams, page, pageSize } = params;
	const { name, ...restQueryParams } = queryParams;

	return apiClient.get('/public/measurabilities', {
		params: {
			page: page,
			pageSize: pageSize,
			query: normalizeQueryParams(restQueryParams),
			sort: `-active${isArec ? ' -supportedBy location.countryCode' : ''} -quantity.remaining_aco2 name`,
			...(name && {
				search: name,
				searchFields: ['name', 'location.place'],
			}),
			active: true,
			populate: `cover, logo, publicSpecs ${isArec ? ', supportedBy.logo' : ''}`,
		},
	});
};

export const getPublicMeasurabilityProjects = (
	params: TApiParams,
	isArec: boolean
): Promise<TApiResponse> => {
	const { queryParams, page, pageSize } = params;
	const { name, ...restQueryParams } = queryParams;
	return apiClient.get('/public/measurabilities', {
		params: {
			page: page,
			pageSize: pageSize,
			query: normalizeQueryParams(restQueryParams),
			active: true,
			sort: `${isArec ? '-supportedBy ' : ''}-quantity.remaining_aco2 name`,
			...(name && {
				search: name,
				searchFields: ['name', 'location.place'],
			}),
			populate: `cover, logo, publicSpecs ${isArec ? ', supportedBy.logo' : ''}`,
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
export const useMeasurabilityProjects = (
	page: number,
	size: number,
	queryParams: TParams
) => {
	const {
		checkAcl,
		checkRole,
		user: cachedUser,
	} = useAuthorization();

	const { t } = useTranslation();

	const isArec: boolean =
		!!cachedUser?.organization &&
		Object.values(AREC_ORGANIZATION_IDS).includes(
			cachedUser.organization
		);

	const { data, isFetching, isFetched, isPending } =
		useQuery({
			queryKey: [
				'measurability-projects',
				{
					page: page,
					size: size,
					queryParams: queryParams,
				},
			],
			queryFn: () => {
				if (
					!checkRole([
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
						ROLES.PRODUCER_ADMIN,
						ROLES.PRODUCER_STAFF,
						ROLES.ORGANIZATION_ADMIN,
						ROLES.ORGANIZATION_STAFF,
						ROLES.BRAND_ADMIN,
						ROLES.BRAND_STAFF,
					]) &&
					!checkAcl(ACLS.MEASURABILITIES.LIST)
				) {
					return getPublicMeasurabilityProjects(
						{
							page: page,
							pageSize: size,
							queryParams: queryParams,
						},
						isArec
					);
				} else if (
					checkRole([
						ROLES.BRAND_ADMIN,
						ROLES.BRAND_STAFF,
					])
				) {
					return getPublicMeasurabilityProjectsForBrands(
						{
							page: page,
							pageSize: size,
							queryParams: queryParams,
						},
						isArec
					);
				}
				return getMeasurabilityProjects(
					{
						page: page,
						pageSize: size,
						queryParams: queryParams,
					},
					isArec
				);
			},
			staleTime: 0,
		});

	const updateList = (projects: TMesurabilityProject[]) =>
		projects.reduce(
			(
				acc: TParsedMesurabilityProject[],
				currentItem: TMesurabilityProject
			) => {
				const technolgyProject = getTechnologyProject(
					currentItem.technology
				);
				const countryName = getCountryName(
					currentItem.location.countryCode
				);

				const cover =
					currentItem && currentItem.cover
						? currentItem.cover
						: technolgyProject
							? technolgyProject.image
							: '';

				const modifiedItem: TParsedMesurabilityProject = {
					...currentItem,
					id: currentItem.id,
					name: currentItem.name,
					country: {
						name: countryName,
						code: currentItem.location.countryCode,
					},
					region: currentItem.location.place,
					type: technolgyProject
						? technolgyProject.label
						: '',
					priceTco2: formatCurrency(
						currentItem.sellPrice_europertco2eq,
						t('global.sellPriceUnit')
					),
					availableStock: convertGCO2EToTCO2EWithoutUnit(
						currentItem?.quantity?.remaining_aco2 || 0
					),
					sellPrice:
						currentItem.sellPrice_europertco2eq || 0,
					tokenAvailable:
						currentItem.quantity &&
						currentItem.quantity.remaining_aco2
							? formatTokenAvailable(
									convertGCO2EToTCO2EWithoutUnit(
										currentItem.quantity.remaining_aco2
									)
								)
							: '0',

					image: cover,
					quantitytco2eq: 0,
					sellPrice_europertco2eq:
						currentItem.sellPrice_europertco2eq,
					isDisabled:
						currentItem.quantity &&
						currentItem.quantity.remaining_aco2
							? formatTokenAvailable(
									convertGCO2EToTCO2EWithoutUnit(
										currentItem.quantity.remaining_aco2
									)
								) == '0'
							: false,
					entity: '',
					unit: '',
					technology: '',
					__t: '',
					costPrice_europertco2eq: 0,
					quantity: {
						total_aco2: 0,
						held_aco2: 0,
						burnt_aco2: 0,
						remaining_aco2: 0,
						unprocessed_gco2eq: 0,
					},
					organization: '',
					createdAt: '',
					updatedAt: '',
					supportedBy: currentItem?.supportedBy?.logo,
					active: currentItem.active,
				};
				acc.push(modifiedItem);

				return acc;
			},
			[]
		);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetching,
		isFetched,
		isPending,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
